/**
 * The session handshake (00-contracts §13.1).
 *
 * Supabase Auth returns a token; `POST /v1/auth/session` is the first call the client makes with
 * it. That one endpoint provisions the person, records any consent and reports what is still
 * outstanding, in a single server transaction. It is idempotent, which is what lets the client
 * call it after every sign-in without special-casing "first time".
 */
import {
  createSessionRequestSchema,
  sessionResponseSchema,
  type CreateSessionRequest,
  type SessionResponse,
} from '@tvitvi/shared';
import type { QueryClient } from '@tanstack/react-query';

import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

import { setActiveOrganizationId } from './active-org';
import { leaveGuestOrganization } from './guest-org';
import { supabase } from './supabase';

export async function createSession(input: CreateSessionRequest = {}): Promise<SessionResponse> {
  const body = createSessionRequestSchema.parse(input);
  const session = await apiRequest({
    path: '/auth/session',
    method: 'POST',
    body,
    schema: sessionResponseSchema,
    sendOrganizationHeader: false,
  });
  setActiveOrganizationId(session.activeOrganizationId);
  return session;
}

/**
 * Called after every successful sign-in. `me` is invalidated rather than written from the
 * session payload: the two DTOs overlap but are not the same, and one authoritative fetch is
 * cheaper than a subtle divergence between them.
 */
export async function bootstrapSession(
  queryClient: QueryClient,
  input: CreateSessionRequest = {},
): Promise<SessionResponse> {
  const session = await createSession(input);
  await queryClient.invalidateQueries({ queryKey: qk.all });
  return session;
}

/**
 * Sign-out. The API records the event and the audit row; it deliberately does not revoke the
 * Supabase refresh token server-side, because that needs a service key this system does not have
 * (§13.1, C-16). The client clears its own session and the whole query cache.
 */
export async function endSession(queryClient: QueryClient): Promise<void> {
  try {
    await apiRequest({ path: '/auth/session', method: 'DELETE', sendOrganizationHeader: false });
  } catch {
    // A failed logout call must never trap someone in a signed-in shell. The local session is
    // cleared regardless, and the 15-minute token TTL bounds the rest.
  }
  await supabase.auth.signOut();
  setActiveOrganizationId(null);
  leaveGuestOrganization();
  queryClient.clear();
}
